import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Project } from '@terafile/shared'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AccessLogsPreview } from '../components/AccessLogsPreview.js'
import { CreateShareLinkDialog } from '../components/CreateShareLinkDialog.js'
import { DeleteProjectModal } from '../components/DeleteProjectModal.js'
import { FileList } from '../components/FileList.js'
import { FileUpload } from '../components/FileUpload.js'
import { MoveToTrashModal, type TrashTarget } from '../components/MoveToTrashModal.js'
import { ShareLinksList } from '../components/ShareLinksList.js'
import { FETCH_ERROR_FALLBACK, SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { listFiles } from '../lib/files-api.js'
import { LOADING, TRASH_ACTION, getTrashRemainingDays } from '../lib/labels.js'
import { useMeTenant } from '../lib/me-api.js'
import { archiveProject, getProject, unarchiveProject, updateProject } from '../lib/projects-api.js'
import { listShareLinks } from '../lib/share-links-api.js'
import { useToast } from '../lib/toast.js'

/**
 * プロジェクト司令室。
 *
 * デザイン方針: エディトリアル × プロフェッショナル。
 *   - ヒーロー: 大胆なタイトル、ステータス、メタ統計を1段で示す
 *   - アクションは右肩に集約、ステルス的な存在感
 *   - セクション境界は番号付きの章マーカーで区切る
 *   - 情報密度は中庸、余白で呼吸を作る
 */
export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const projectId = id ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [trashTarget, setTrashTarget] = useState<TrashTarget | null>(null)

  const projectQ = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => getProject(projectId),
    enabled: !!projectId,
  })

  // 統計用にファイル一覧 / 共有URL一覧を別 query で取得（FileList/ShareLinksList と同じ key で cache 共有）
  const filesQ = useQuery({
    queryKey: ['files', projectId],
    queryFn: () => listFiles(projectId),
    enabled: !!projectId,
  })
  const shareLinksQ = useQuery({
    queryKey: ['share-links', projectId],
    queryFn: () => listShareLinks(projectId),
    enabled: !!projectId,
  })
  // 自テナント情報（プラン依存 UI 用）
  const meTenantQ = useMeTenant()
  // PRO のみ: プロジェクト単位完全削除（F-DEL-02）
  const canDeleteProject = meTenantQ.data?.plan === 'PRO'

  const invalidateProject = () => {
    queryClient.invalidateQueries({ queryKey: ['projects', projectId] })
    queryClient.invalidateQueries({ queryKey: ['projects'] })
  }
  const archiveMut = useMutation({
    mutationFn: () => archiveProject(projectId),
    onSuccess: () => {
      invalidateProject()
      toast.success(TRASH_ACTION.toastMovedProject)
      setTrashTarget(null)
    },
    onError: (err) => {
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })
  const unarchiveMut = useMutation({
    mutationFn: () => unarchiveProject(projectId),
    onSuccess: () => {
      invalidateProject()
      toast.success(TRASH_ACTION.toastRestoredProject)
    },
    onError: (err) => {
      toast.error(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-16 space-y-12 md:space-y-16 grain">
      {projectQ.isLoading && <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>}
      {projectQ.error && (
        <ErrorBanner message={toUserMessage(projectQ.error, FETCH_ERROR_FALLBACK)} />
      )}
      {projectQ.data && (
        <>
          <Hero
            project={projectQ.data}
            fileCount={filesQ.data?.total ?? 0}
            totalBytes={filesQ.data?.items.reduce((sum, f) => sum + Number(f.sizeBytes), 0) ?? 0}
            shareLinkCount={shareLinksQ.data?.items.length ?? 0}
            onMoveToTrash={() =>
              setTrashTarget({
                kind: 'project',
                id: projectQ.data.id,
                name: projectQ.data.name,
              })
            }
            onRestore={() => unarchiveMut.mutate()}
            onPurgeNow={() => setDeleteOpen(true)}
            busy={archiveMut.isPending || unarchiveMut.isPending}
            canDeleteProject={canDeleteProject}
          />

          <Section
            num="01"
            title="ファイル"
            hint="アップロード後、動画は自動でストリーミング配信用に変換されます"
          >
            <FileUpload projectId={projectId} projectName={projectQ.data.name} />
            <div className="mt-6">
              <FileList projectId={projectId} />
            </div>
          </Section>

          <Section
            num="02"
            title="共有URL"
            hint="社外スタッフへ閲覧 / DL 用のリンクを発行できます。期限・権限は個別設定。"
            action={
              <button
                type="button"
                onClick={() => setShareDialogOpen(true)}
                className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity"
              >
                共有URLを作成
              </button>
            }
          >
            <ShareLinksList projectId={projectId} />
          </Section>

          <Section
            num="03"
            title="アクセスログ"
            hint="ファイルの閲覧 / ダウンロード / アップロードの履歴を時系列で確認できます。"
            action={
              <Link
                to={`/projects/${projectId}/access-logs`}
                className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors"
              >
                履歴をすべて見る
                <span aria-hidden>→</span>
              </Link>
            }
          >
            <AccessLogsPreview projectId={projectId} />
          </Section>

          <CreateShareLinkDialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            projectId={projectId}
          />
          <MoveToTrashModal
            target={trashTarget}
            onClose={() => setTrashTarget(null)}
            onConfirm={() => archiveMut.mutate()}
            isPending={archiveMut.isPending}
          />
          <DeleteProjectModal
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            project={projectQ.data}
            onDeleted={() => navigate('/projects')}
          />
        </>
      )}
    </div>
  )
}

// ===========================================================================
// 視覚部品
// ===========================================================================

function Hero({
  project,
  fileCount,
  totalBytes,
  shareLinkCount,
  onMoveToTrash,
  onRestore,
  onPurgeNow,
  busy,
  canDeleteProject,
}: {
  project: Project
  fileCount: number
  totalBytes: number
  shareLinkCount: number
  onMoveToTrash: () => void
  onRestore: () => void
  onPurgeNow: () => void
  busy: boolean
  /** PRO プランでのみプロジェクト完全削除可。FREE / EPHEMERAL は不可（F-DEL-02） */
  canDeleteProject: boolean
}) {
  const isInTrash = !!project.archivedAt
  const trashRemainingDays = isInTrash ? getTrashRemainingDays(project.archivedAt as string) : null

  return (
    <section>
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          {/* 微小な kicker。
              旧: `Project · <UUID 短縮>` を出していたがユーザに意味が無いので
              「作成日」に置き換え。ID は内部識別子なので画面では使わない。 */}
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground/80">
              Project · 作成 {new Date(project.createdAt).toLocaleDateString('ja-JP')}
            </span>
            {isInTrash && trashRemainingDays !== null && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 border border-amber-200 rounded-full text-amber-900 bg-amber-50">
                {TRASH_ACTION.trashedBadge} · 残 {trashRemainingDays} 日
              </span>
            )}
          </div>

          {/* タイトル: ここが視覚的アンカー。
              ゴミ箱内でない時はインライン編集できる（ペンアイコンで気付けるように）。 */}
          <EditableTitle project={project} disabled={isInTrash} />

          {/* 統計バー */}
          <dl className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-8 gap-y-4 w-full md:max-w-2xl">
            <Stat label="ファイル" value={fileCount.toLocaleString('ja-JP')} unit="件" />
            <Stat label="合計サイズ" value={formatBytes(totalBytes)} />
            <Stat label="共有URL" value={shareLinkCount.toLocaleString('ja-JP')} unit="本" />
            <Stat
              label="最終更新"
              value={new Date(project.updatedAt).toLocaleDateString('ja-JP')}
            />
          </dl>
        </div>

        {/* アクション群: 右肩、控えめ。
            共有URLの作成は「02 共有URL」セクションで行うため、ここには重複表示しない。 */}
        <div className="flex items-center gap-2 flex-wrap shrink-0 border-t border-border pt-4 md:border-t-0 md:pt-0 md:pl-0">
          {isInTrash ? (
            <>
              <button
                type="button"
                onClick={onRestore}
                disabled={busy}
                className="px-3 py-2 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
              >
                {TRASH_ACTION.restore}
              </button>
              {canDeleteProject ? (
                <button
                  type="button"
                  onClick={onPurgeNow}
                  className="px-3 py-2 border border-destructive/40 text-destructive rounded-md text-sm font-medium hover:bg-destructive/5 transition-colors"
                >
                  {TRASH_ACTION.purgeNow}
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  title="プロジェクトの完全削除は有料プラン専用です"
                  className="px-3 py-2 border border-border text-muted-foreground rounded-md text-sm font-medium opacity-60 cursor-not-allowed"
                >
                  {TRASH_ACTION.purgeNow}（PRO）
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              onClick={onMoveToTrash}
              disabled={busy}
              className="px-3 py-2 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
            >
              {TRASH_ACTION.delete}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

function Stat({
  label,
  value,
  unit,
}: {
  label: string
  value: string
  unit?: string
}) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-mono">
        {label}
      </dt>
      <dd className="mt-1 font-display text-xl md:text-2xl font-semibold tracking-tight editorial-num">
        {value}
        {unit && (
          <span className="ml-1 text-xs text-muted-foreground font-sans font-normal">{unit}</span>
        )}
      </dd>
    </div>
  )
}

function Section({
  num,
  title,
  hint,
  action,
  children,
}: {
  num: string
  title: string
  hint?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between border-b border-border pb-4">
        <div className="flex items-end gap-4 min-w-0">
          <span className="font-mono text-xs text-muted-foreground/70 editorial-num pb-1">
            {num}
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight">
              {title}
            </h2>
            {hint && <p className="text-xs text-muted-foreground mt-1 max-w-prose">{hint}</p>}
          </div>
        </div>
        {action && <div className="shrink-0 w-full sm:w-auto">{action}</div>}
      </div>
      <div>{children}</div>
    </section>
  )
}

/**
 * プロジェクト名のインライン編集 UI。
 *
 * - 通常: タイトルとペンアイコン。クリック or Enter で編集モードへ。
 * - 編集中: input + 保存 / キャンセル。Enter で保存、Esc でキャンセル。
 * - 入力が空 or 200 文字超は保存ボタンを無効化。
 * - サーバ更新成功時はクエリキャッシュを差し替えてヘッダ等にも即反映する。
 * - ゴミ箱内のプロジェクトは編集不可
 */
function EditableTitle({ project, disabled }: { project: Project; disabled: boolean }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(project.name)
  const [error, setError] = useState<string | null>(null)

  const renameMut = useMutation({
    mutationFn: (newName: string) => updateProject(project.id, { name: newName }),
    onSuccess: (updated) => {
      // 詳細キャッシュを差し替え + 一覧キャッシュも陳腐化させる
      queryClient.setQueryData(['projects', project.id], updated)
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('プロジェクト名を更新しました')
      setEditing(false)
    },
    onError: (err) => {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  const startEdit = () => {
    if (disabled) return
    setDraft(project.name)
    setError(null)
    setEditing(true)
  }

  const cancel = () => {
    setEditing(false)
    setError(null)
  }

  const save = () => {
    const trimmed = draft.trim()
    if (!trimmed) {
      setError('プロジェクト名を入力してください')
      return
    }
    if (trimmed.length > 200) {
      setError('プロジェクト名は 200 文字以内にしてください')
      return
    }
    if (trimmed === project.name) {
      // 変更なしならそのまま閉じる（無駄な API 呼び出しを避ける）
      setEditing(false)
      return
    }
    renameMut.mutate(trimmed)
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
          {project.name}
        </h1>
        {!disabled && (
          <button
            type="button"
            onClick={startEdit}
            aria-label="プロジェクト名を編集"
            className="text-sm px-3 py-1.5 border border-border rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
          >
            <span aria-hidden className="mr-1.5">
              ✎
            </span>
            名前を変更
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value)
          setError(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            save()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            cancel()
          }
        }}
        maxLength={200}
        // biome-ignore lint/a11y/noAutofocus: 編集モードに入った瞬間にフォーカスを当てる意図的な UX
        autoFocus
        disabled={renameMut.isPending}
        className="w-full font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight bg-transparent border-b-2 border-primary/30 focus:border-primary outline-none disabled:opacity-50"
        aria-label="プロジェクト名"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={save}
          disabled={renameMut.isPending || !draft.trim() || draft.trim() === project.name}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          {renameMut.isPending ? '保存中…' : '保存'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={renameMut.isPending}
          className="px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-accent transition-colors"
        >
          キャンセル
        </button>
        <span className="text-xs text-muted-foreground">Enter: 保存 · Esc: キャンセル</span>
      </div>
      {error && (
        <p className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2">
          {error}
        </p>
      )}
    </div>
  )
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="border border-destructive/40 bg-destructive/5 rounded-md p-4 text-sm text-destructive">
      {message}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  if (bytes < 1024 ** 4) return `${(bytes / 1024 ** 3).toFixed(2)} GB`
  return `${(bytes / 1024 ** 4).toFixed(2)} TB`
}
